import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, TextField, Dialog, DialogActions,InputAdornment, DialogContent, DialogTitle, IconButton, Box, Typography, Tooltip } from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import EditableTable from '../../customTableComponent/index';
import AddIcon from '@mui/icons-material/AddCircleOutline';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import { proceduresList, proceduresListType } from './proceduresHelper';
import ProcedureDialog from './ProcedureDialog'; 
import CycleList from '../CycleList/cycleList';
import { getProcedures } from '../../../services/apiService';
import { convertDateToDDMMYYYY, convertUnderscoreToTitle } from '../../../utils/commonUtils';
import { useLocation } from 'react-router-dom';
import {ReactComponent as PrinterIcon} from '../../../assets/SVG/printer (1).svg';
import PrintReport from '../../printReport';
import { usePrint } from '../../PrintProvider';
import { useVerboseLog } from '../../../utils/debug';
import { TreatmentPlanTypePermission } from '../../../pages/TreatmentPlanPage/treatmentPlan.helper';
import RemarkPopover from '../../Billing/common/RemarkPopover';
import { ReactComponent as SearchIcon } from '../../../assets/SVG/Search-Icon.svg';

interface PatientListProps {
  patientId: number | null;
  permissionObj: TreatmentPlanTypePermission | undefined;
}

const ProcedureList: React.FC<PatientListProps> = ({ patientId, permissionObj }) => {

  const { TabOption, setTabOption } = usePrint(); 
  const verboseLog = useVerboseLog();

  const [tableData, setTableData] = useState<proceduresListType[]>(proceduresList); 
  const [selectedProcedure, setSelectedProcedure] = useState<proceduresListType | null>(null); 
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false); // For Delete confirmation
  const [procedureToDelete, setProcedureToDelete] = useState<proceduresListType | null>(null); // For storing the procedure to delete
  const [isCycleListVisible, setIsCycleListVisible] = useState(false); 
  const [searchTerm, setSearchTerm] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [showPrint, setShowPrint] = useState<boolean>(false);
  
  // Procedure Notes popover state
  const [notesAnchorEl, setNotesAnchorEl] = useState<null | HTMLElement>(null);
  const [notesText, setNotesText] = useState<string>('');

  const handleOpenNotes = (event: React.MouseEvent<HTMLElement>, text: string) => {
    event.stopPropagation();
    setNotesAnchorEl(event.currentTarget);
    setNotesText(text);
  };

  const handleCloseNotes = () => {
    setNotesAnchorEl(null);
    setNotesText('');
  };
  
  // Translation hooks
  const { t: tGeneral } = useTranslation('general');
  const { t: tTP } = useTranslation('treatmentPlan_config');

  // Localized columns and groupConfig
  const columns = [
    { field: 'advised_date', headerName: tGeneral('advisedDate'), type: 'text' as const, editable: false, hideLabel: true },
    { field: 'procedures_type', headerName: tGeneral('procedureType'), type: 'text' as const, editable: false, hideLabel: true },
    { field: 'service_code', headerName: tGeneral('serviceCode'), type: 'text' as const, editable: false, hideLabel: true },
    { field: 'service_name', headerName: tGeneral('serviceName'), type: 'text' as const, editable: false, hideLabel: true },
    { field: 'clinic', headerName: tGeneral('Clinic'), type: 'text' as const, editable: false, hideLabel: true },
    { field: 'procedure_notes', headerName: tGeneral('notes'), type: 'text' as const, editable: false, hideLabel: true },
  ];

  const groupConfig = [
    { fields: ['advised_date'], type: 'row' as const },
    { fields: ['procedures_type'], type: 'column' as const, defaultHeaderGroupname: tGeneral('procedureType') },
    { fields: ['service_code'], type: 'column' as const, defaultHeaderGroupname: tGeneral('serviceCode') },
    { fields: ['service_name'], type: 'column' as const, defaultHeaderGroupname: tGeneral('serviceName') },
    { fields: ['clinic'], type: 'column' as const, defaultHeaderGroupname: tGeneral('Clinic') },
    { fields: ['procedure_notes'], type: 'column' as const, defaultHeaderGroupname: tGeneral('procedureNotes') },
  ];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleFetchedData = (data: any[]) => {
    setTableData(data); 
  };

  const hasAccess = (type: string) => {
    if (!permissionObj || !permissionObj.permissions) return false;
    return permissionObj.permissions.some((perm) => perm.access === type);
  };

  const canAdd = hasAccess('add');
  const canPrint = hasAccess('print');
  const canEditOrView = hasAccess('edit') || hasAccess('view');
  const canEdit = hasAccess('edit');
  
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const fetchProcedures = useCallback((callback: (data: any[]) => void) => {
    setTableData([]); 
    if (patientId) {
      const query = `?patient=${patientId}`;
      getProcedures(query)
        .then((data) => {
          
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const transformedData = data.objects.map((item: any) => {
            const notes = item.procedure_notes ?? '-';
            return {
              id: item.id,
              advised_date: convertDateToDDMMYYYY(item.created_on) ?? '-',
              procedures_type: item?.procedures_label ?? '-',
              service_code: !item.cycle_type ? item.services[0]?.code : '-',
              service_name: !item.cycle_type ? item.services[0]?.name_label : '-',
              clinic: item.clinic || '-',
              procedure_notes: typeof notes === 'string' && notes.length > 30 ? (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <Tooltip title={tGeneral('Procedure Notes')} arrow>
                    <IconButton
                      size="small"
                      sx={{ p: 0, color: '#5A8AEA', flexShrink: 0 }}
                      aria-label={tGeneral('Procedure Notes')}
                      onClick={(e: React.MouseEvent<HTMLElement>) => handleOpenNotes(e, notes)}
                    >
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Typography>{notes.slice(0, 30)}...</Typography>
                </Box>
              ) : notes,
              services: item.services ?? '-',
              indication: item.indication ? convertUnderscoreToTitle(item.indication) : '-',
              indication_label: item.indication_label ?? '-',
              doctor_name: item?.doctor_name,
              donor_info: item?.donor_info,
              created_on: item?.created_on,
              doctor: item?.doctor,
              procedure_id: item.procedure_id
            };
          });
          callback(transformedData);
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error('Error fetching data:', error);
        });
    }
  }, [patientId, tGeneral]);

  useEffect(() => {
    fetchProcedures(handleFetchedData); 
  }, [patientId, fetchProcedures]);

  
  const location = useLocation();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [view, setView] = useState(new URLSearchParams(location.search).get('view'));
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    setView(queryParams.get('view'));

    if (queryParams.get('view') === 'procedure_list') {
      setIsCycleListVisible(false);
      fetchProcedures(handleFetchedData);


    }
  }, [location, fetchProcedures]);

  useEffect(() => {
    fetchProcedures(handleFetchedData); // Call fetchProcedures with the callback


  }, [patientId, fetchProcedures]);
  const handleTableChange = (id: number, field: string, value: string | number) => {
    setTableData((prevData) =>
      prevData.map((row) =>
        row.id === id ? { ...row, [field]: value } : row
      )
    );
  };

  const handleRowClick = (id: number) => {
    const rowData = tableData.find((procedure) => procedure.id === id);
    
    if (rowData) {
      setSelectedProcedure(rowData); // Set the selected procedure for editing
      setIsDialogOpen(true); // Open the edit dialog
    }
  };

  const handleDeleteClick = (id: number) => {
    const procedureToDelete = tableData.find((procedure) => procedure.id === id);
    if (procedureToDelete) {
      setProcedureToDelete(procedureToDelete); // Set the procedure to be deleted
      setIsDeleteDialogOpen(true); // Open delete confirmation dialog
    }
  };

  const handleClose = () => {
    setIsDialogOpen(false);
    setSelectedProcedure(null);
    fetchProcedures(handleFetchedData); // Call fetchProcedures with the callback

  };

  const handleAddButtonClick = () => {
    setIsCycleListVisible(true); 
    // Update the 'view' query parameter to 'select_procedure'
    const queryParams = new URLSearchParams(location.search);
    if (queryParams.has('view')) {
      queryParams.delete('view'); // Remove the 'view' parameter
      
      const newQueryString = queryParams.toString();
      window.history.replaceState(
        null,
        '',
        `${window.location.pathname}${newQueryString ? `?${newQueryString}` : ''}`
      );
    }
  };

  const handleSaveProcedure = (updatedProcedure: proceduresListType) => {
    setTableData((prevData) =>
      prevData.map((row) =>
        row.id === updatedProcedure.id ? updatedProcedure : row
      )
    );
  };

  const handleCloseCycleList = () => {
    setIsCycleListVisible(false);
  };

  const handleDeleteProcedure = () => {
    if (procedureToDelete) {
      setTableData((prevData) =>
        prevData.filter((row) => row.id !== procedureToDelete.id) 
      );
      setIsDeleteDialogOpen(false);
      setProcedureToDelete(null);
    }
  };

  const handleDeleteDialogClose = () => {
    setIsDeleteDialogOpen(false);
    setProcedureToDelete(null);
  };

  return (
    <div>
      {showPrint && <PrintReport handleClose={() => setShowPrint(false)} />}
      {isCycleListVisible ? (
        <CycleList onClose={handleCloseCycleList} />
      ) : (
        <Grid2 container spacing={2} my={1} mx={3} alignItems="center">
          <Grid2 container spacing={2} my={1} mx={1} size={12} alignItems="center">
            {canEditOrView && (
              <Grid2 size="grow">
                <p style={{ fontWeight: 600, fontFamily: 'Nunito', fontSize: '18px', margin: 0 }}>{tTP('listOfPrescribedServices')}</p>
              </Grid2>
            )}
            {canEditOrView && (
              <Grid2 size={3}>
                <Tooltip
                  title={tableData.length === 0 ? tTP('searchDisabledNoData') : ''}
                  arrow placement="bottom">
                  <Box
                    style={{ cursor: tableData.length === 0 ? 'not-allowed' : 'default' }}
                    tabIndex={tableData.length === 0 ? 0 : undefined}
                    aria-disabled={tableData.length === 0 ? true : undefined}
                    aria-label={tableData.length === 0 ? tTP('searchDisabledNoData') : undefined}>
                    <TextField
                      variant="outlined"
                      placeholder={tTP('searchPlaceholder')}
                      label={tTP('searchPlaceholder')}
                      disabled={tableData.length === 0}
                      size="small"
                      fullWidth
                      value={searchTerm}
                      onChange={(event) => setSearchTerm(event.target.value)}
                      onFocus={() => setSearchFocused(true)}
                      onBlur={() => setSearchFocused(false)}
                      slotProps={{
                        input: {
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon />
                            </InputAdornment>
                          )
                        },
                        inputLabel: {
                          shrink: searchFocused || searchTerm.length > 0,
                        },
                      }}
                      sx={{
                        pointerEvents: tableData.length === 0 ? 'none' : 'auto',
                        '& .MuiInputLabel-root:not(.MuiInputLabel-shrink)': {
                          transform: 'translate(50px, 10px) scale(1)',
                        },
                      }}
                    />
                  </Box>
                </Tooltip>
              </Grid2>)}
            {canAdd && (
              <Grid2 size="auto">
                <Button
                  variant="contained"
                  color="primary"
                  sx={{ textTransform: 'none', fontWeight: 'bold' }}
                  startIcon={<AddIcon />}
                  onClick={handleAddButtonClick}
                >
                  {tGeneral('Add')}
                </Button>
              </Grid2>
            )}
            {/* Printer Icon Button */}
            {canPrint && (
              <Grid2 size="auto">
                <Tooltip title='Print Report'>
                  <IconButton onClick={() => {
                    setShowPrint(true);
                    setTabOption({
                      tabName: 'treatment-plan',
                      subOption: 'procedure_list'
                    });
                    verboseLog('Print button clicked for Procedure List', TabOption);
                  }}>
                    <PrinterIcon style={{ width: '24px', height: '24px' }} />
                  </IconButton>
                </Tooltip>
              </Grid2>
            )}
          </Grid2>
          {canEditOrView && (
            <Grid2 size={12}>
              <EditableTable
                columns={columns}
                data={tableData.filter(item => (item.services[0]?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                      item.procedures_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                      item.services[0]?.code?.toLowerCase().includes(searchTerm.toLowerCase()))
                )}
                actionType="delete"
                groupConfig={groupConfig}
                onChange={handleTableChange}
                onRowClick={handleRowClick}
                onAction={handleDeleteClick}
                noDataText={tTP('noProceduresFound')} />
            </Grid2>
          )}
          {/* Procedure Dialog */}
          <ProcedureDialog
            open={isDialogOpen}
            canEdit={canEdit}
            handleClose={handleClose}
            procedureData={selectedProcedure!}
            onSave={handleSaveProcedure}
          />
          {/* Delete Confirmation Dialog */}
          <Dialog open={isDeleteDialogOpen} onClose={() => handleDeleteDialogClose}>
            <DialogTitle>{tTP('confirmDeletion')}</DialogTitle>
            <DialogContent>{tTP('areYouSureYouWantToDeleteProcedure')}</DialogContent>
            <DialogActions>
              <Button onClick={() => handleDeleteDialogClose()} color="primary">
                {tGeneral('cancel')}
              </Button>
              <Button onClick={handleDeleteProcedure} color="primary">
                {tGeneral('delete')}
              </Button>
            </DialogActions>
          </Dialog>
        </Grid2>
      )}
      <RemarkPopover
        anchorEl={notesAnchorEl}
        content={notesText}
        open={Boolean(notesAnchorEl)}
        onClose={handleCloseNotes}
        title={tGeneral('procedureNotes')}
      />
    </div>
  );
};

export default ProcedureList;
